import React, { useEffect, useState, useContext } from "react";
import GlobalStyles, {
  ThemeProvider,
  Theme,
  DynamicStyles,
} from "./views/common/Resources/GlobalStyle";

// App Provider
import { AppProvider, AppContext } from "./components/AppProvider";

// Apollo Cache Enable
import { InMemoryCache } from "apollo-cache-inmemory";
import { persistCache } from "apollo-cache-persist";
import ApolloClient from "apollo-boost";
import { ApolloProvider } from "@apollo/react-hooks";
import { ApolloOptions } from "./modules/config";

// Utils
import { _CK, _API } from "./modules/utils";

// UI
import { Loading } from "./components/UI/Loading";
import LoadingOverlay from "react-loading-overlay";
import LoadingShimmer from "./components/UI/LoadingShimmer";
// Router
import Views from "./views";
import useStore from "./modules/utils/store";
import { delay } from "lodash";
import hostUtils from "./modules/utils/playerUnmount";
import BrowserStorageBlocked from "./components/UI/BrowserStorageBlocked";
import {
  isBrowserStorageBlocked,
  isLocalStorageAvailable,
} from "./modules/utils/browserStorage";

export default (props) => {
  const { resolveConsoleSiteDomain } = hostUtils;
  // 쿠키 차단(Edge "쿠키 데이터 허용" OFF, 그룹정책 등) 환경에서는 인증이 불가능하므로
  // preLoad 이전에 한 번만 감지한다. 차단 시 preLoad를 건너뛰어 리다이렉트 루프를 막는다.
  const [storageBlocked] = useState(() => isBrowserStorageBlocked());
  const [loaded, setLoaded] = useState(false);
  const [auth, setAuth] = useState("");
  const [iam, setIam] = useState(null);
  const [iamChecked, setIamChecked] = useState(false);
  const [client, setClient] = useState(null);
  const siteDomain = resolveConsoleSiteDomain(window.location);
  const pathName = window.location.hash;
  const {setSiteInfo,setSiteTemplateInfo, setCampaignInfo} = useStore();

  let pathInfos = [];
  let campaignIdx = "";

  if (pathName) {
    pathInfos = pathName.indexOf("?") === -1 ? pathName.split("/") : pathName.split("?")[0].split("/");
    if(pathInfos[1] == 'campaign') {
      campaignIdx = pathInfos[2];
    }
  }

  const campaignLoad = async () => {

    const req = {
      path: "/v1/campaign/info",
      data:{
        idx : campaignIdx,
        noAuthorCheck: true
      }
    };

    _API.post(req).then((res) => {
      const { data } = res.data;
      setCampaignInfo(data);
    });

  }

  const templateLoad = async () => {
    const req = {
      path: "/v1/template/event/template/info",
      data:{
        domain : siteDomain
      }
    };

    _API.post(req).then((res) => {
      setSiteTemplateInfo(res)
    });
  }

  const preLoad = async () => {

 /*    const sqs = {
      path: "/v1/client/sqs",
      data:{
        domain : hostname
      }
    };

    await _API.post(sqs).then((res) => {
      if (res.data) {
        console.log('sqs currentConcurrency ', Number(res.data.data));
        if(Number(res.data.data) > 1){
          alert("대기열에 남은 수 : "+ res.data.data );
        }
      } else {
        
      }
    }); */

    try {
      const cache = new InMemoryCache();
      // DOM Storage가 막힌 환경에서는 window.localStorage 접근만으로 throw하므로,
      // 사용 가능할 때만 영속화하고 아니면 인메모리 캐시만 사용한다.
      if (isLocalStorageAvailable()) {
        try {
          await persistCache({ cache, storage: window.localStorage });
        } catch (e) {
          console.warn("[Console preload] persistCache skipped:", e);
        }
      }
      const ao = ApolloOptions();
      const client = new ApolloClient({
        cache,
        ...ao,
      });

      // 다이렉트 로그인 파라미터 확인 (au가 있으면 directLogin에서 새로 로그인 처리)
      const urlParams = new URLSearchParams(window.location.hash.split('?')[1]);
      const hasDirectLogin = urlParams.get('au');

      const hasPersistedConsoleSession = _CK.hasConsoleSession();

      // F5 새로고침 복원: 쿠키가 있으면 snapshot(=hasConsoleSession) 일치 여부와 무관하게
      // iam으로 복원을 시도한다. 실제 유효성은 /v1/client/iam 가 판정하며(쿠키 주석과 동일),
      // 유효하면 setAuth 로 snapshot 도 재동기화된다. (snapshot 불일치로 F5 시 즉시 로그아웃되던 문제 해결)
      // 콘솔보기(au 직접로그인)는 hasDirectLogin=true 라 이 분기를 타지 않는다(directLogin 미간섭).
      if (_CK.getAuth() && !hasDirectLogin) {
        // ✅ URL에서 eventIdx 추출 (직접 URL 접근 시 대비)
        // 예: /#/event/1/session/1/default → pathInfos[2] = '1'
        let urlEventIdx = null;
        if (pathInfos[1] === 'event' && pathInfos[2]) {
          urlEventIdx = parseInt(pathInfos[2]);
        }
        try {
          const iamRes = await _API.iam(null, urlEventIdx, { isNoErrorNotify: true });
          let { data: newIam, accessKey } = iamRes.data;
          if (newIam) {
            _CK.setAuth(accessKey);
            // 🎯 서버에서 받은 logKey를 쿠키에 저장 (기존 유지 또는 새 생성)
            if (newIam.logKey) {
              _CK.forceSetLogKey(newIam.logKey);
            } else {
              // IAM이 null logKey 반환 시 → cookie 값으로 복원 (방어 레이어)
              newIam = { ...newIam, logKey: _CK.getLogKey() };
            }
            setAuth(accessKey);
            setIam(newIam);
          } else {
            _CK.clearAuth();
          }
        } catch (e) {
          console.warn("[Console preload] Stored auth is invalid or expired. Clearing session.", e);
          _CK.clearAuth();
        } finally {
          setIamChecked(true);
        }
      } else {
        if (_CK.getAuth() && !hasDirectLogin && !hasPersistedConsoleSession) {
          _CK.clearAuth();
        }
        setIamChecked(true);
      }

      const req = {
        path: "/v1/template/event/template/info",
        data:{
          domain : siteDomain
        }
      };
      await _API.post(req).then((res) => {
        if (res.data) {
          setSiteInfo(res.data)
          $('.web-title').text(res.data?.title);
        } else {
          setLoaded(true);
          throw new Error("사이트를 찾지못했습니다.");
        }
      });

      setClient(client);
      setLoaded(true);
    } catch (e) {
      console.error("APP PreLoad Error", e);
      _CK.clearAuth();
      // 이미 루트에서 실패했다면 루트로 다시 보내도 같은 실패가 반복된다.
      // HashRouter 라 "/" 와 "/#/" 를 오가며 전체 페이지가 재로드되고,
      // 재시도 상한이 없어 사이트 조회 API 로 요청이 무한히 나간다.
      // 미등록 host(오타 URL, 폐기된 링크, 도메인 전환 중 미매칭 site)가 이 경로를 탄다.
      const hash = document.location.hash;
      const atRoot =
        document.location.pathname === "/" &&
        (!hash || hash === "#" || hash === "#/");
      if (atRoot) {
        return;
      }
      document.location.href = "/";
    }
  };

  useEffect(() => {
    // 쿠키/스토리지 차단 환경: 안내 화면만 표시하고 데이터 로드/리다이렉트는 건너뛴다.
    if (storageBlocked) {
      return;
    }
    templateLoad();
    if(pathInfos[1] == 'campaign') {
      campaignLoad();
    }
    preLoad();
  }, []);

  if (storageBlocked) {
    return <BrowserStorageBlocked />;
  }

  if (!loaded || !client) {
    return (
      <ThemeProvider theme={Theme}>
        <LoadingShimmer />
        <GlobalStyles />
      </ThemeProvider>
    );
  }

  return (
    <div className="tailwind-override" style={{height: '100%'}}>
      <ApolloProvider client={client}>
        <ThemeProvider theme={Theme}>
          <AppProvider iam={iam} iamChecked={iamChecked}>
            <Views />
          </AppProvider>
        </ThemeProvider>
      </ApolloProvider>
    </div>
  );
};
